import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  ListInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectWithSearch,
  Switch,
} from '@ais-ui/ui-core';
import React, { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useGetFilters } from '@ais-ui/websockets';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { ShapeSelect } from '../../../Shapes/ShapeSelects/ShapeSelect';
import { DetectorFormTypes, DetectorType } from './detectorForm';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { DrawGeometryType } from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import { useOwnershipCheck } from '../../../utils/useOwnershipCheck';
import { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { GroundingConfigurationSchema } from '@ais-ui/api-spec/grounding';
import * as Sentry from '@sentry/react';

interface MutateBaseFormProps {
  form: UseFormReturn<DetectorFormTypes>;
  detectorType: DetectorType;
  onStartDrawing: (geometryType: DrawGeometryType, shape?: GeoJson) => void;
  onShowShape: (shape?: GeoJson) => void;
  detector?: EventdetectorDefinition | GroundingConfigurationSchema;
  mutateType?: 'edit' | 'create';
}

function useAllowedGeometryTypes(
  detectorType: DetectorType,
): DrawGeometryType[] {
  const allowedGeometryTypes = useMemo((): DrawGeometryType[] => {
    switch (detectorType) {
      case 'area':
        return ['Polygon', 'Circle'];
      case 'line':
        return ['LineString'];
      case 'cable':
        return ['Polygon'];
      case 'fishing':
        return ['Polygon'];
      default:
        return []; // or return a default value that makes sense for your application
    }
  }, [detectorType]);

  return allowedGeometryTypes;
}

export const MutateBaseForm = React.forwardRef<
  HTMLDivElement,
  MutateBaseFormProps
>(({ form, detectorType, onStartDrawing, onShowShape, detector, mutateType }, ref) => {
  const { messages } = useLocalization();
  const { data: filters } = useGetFilters();
  const { isOwner } = useOwnershipCheck();

  const filterSelections = useMemo(() => {
    const selectionItems = filters
      ? filters.map((filter) => ({
          id: filter.uuid,
          name: filter.name,
        }))
      : [];
    selectionItems.unshift({ id: 'no-filter', name: messages.reportsCreateReportNoFilterValue ?? 'No filter' });
    return selectionItems;
  }, [filters, messages]);

  const allowedGeometryTypes = useAllowedGeometryTypes(detectorType);

  const handleFilterNotFound = useCallback((filterId: string) => {
    Sentry.captureMessage('Filter not found in Detector form', {
      level: 'warning',
      tags: { component: 'MutateBaseForm', context: 'filter-not-found' },
      extra: { missingFilterId: filterId, detectorType },
    });
  }, [detectorType]);

  return (
    <div className="space-y-8 pt-6 px-px">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.eventdetectorsSaveDetectorNameLabel} *
              </FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                id="event-detector-name-field"
                type="text"
                placeholder={messages.eventdetectorsSaveDetectorNamePlaceholder}
                autoFocus
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="enabled"
        render={({ field }) => (
          <FormItem className="flex flex-col space-y-4">
            <FormLabel>
              {messages.eventdetectorsSaveDetectorEnabledLabel}
            </FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <Switch
                id="event-detector-enabled-switch"
                checked={field.value}
                onCheckedChange={(value) => {
                  field.onChange(value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="severity"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between mr-2">
              <FormLabel>
                {messages.eventdetectorsSaveDetectorSeverityLabel}
              </FormLabel>
              <InfoIconTooltip>
                {messages.eventdetectorsSaveDetectorSeverityTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      messages.eventdetectorsSaveDetectorSeverityPlaceholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">
                    <div className="flex items-center">
                      <span className="block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      <p>{messages.eventdetectorsSaveDetectorSeverityLow}</p>
                    </div>
                  </SelectItem>
                  <SelectItem value="warning">
                    <div className="flex items-center">
                      <span className="block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                      <p>{messages.eventdetectorsSaveDetectorSeverityMedium}</p>
                    </div>
                  </SelectItem>
                  <SelectItem value="alert">
                    <div className="flex items-center">
                      <span className="block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      <p>{messages.eventdetectorsSaveDetectorSeverityHigh}</p>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="shapeId"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.eventdetectorsSaveDetectorShapeLabel} *
              </FormLabel>
            </div>
            <div className="flex space-x-4">
              <div className="space-y-2 w-full">
                <FormControl onChange={() => form.clearErrors()}>
                  <ShapeSelect
                    id="event_detector_select_shape"
                    value={field.value}
                    onChange={field.onChange}
                    allowedGeometryTypes={allowedGeometryTypes}
                  ></ShapeSelect>
                </FormControl>
                <FormMessage />
              </div>
            </div>
          </FormItem>
        )}
      />

      {detectorType !== 'grounding' && (
        <FormField
          control={form.control}
          name="filterId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {messages.eventdetectorsSaveDetectorFilterLabel}
              </FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <SelectWithSearch
                  value={field.value as string}
                  onChange={field.onChange}
                  data={filterSelections}
                  placeholder={
                    messages.eventdetectorsSaveDetectorFilterPlaceholder
                  }
                  notFoundText={messages.filterNotFound || 'Filter not found'}
                  onNotFound={handleFilterNotFound}
                ></SelectWithSearch>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {detectorType !== 'grounding' && (
        <FormField
          control={form.control}
          name="maintainShiplist"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-4">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsSaveDetectorMaintainShiplistLabel}
                </FormLabel>
                <InfoIconTooltip>
                  {messages.eventdetectorsSaveDetectorMaintainShiplistTooltip}
                </InfoIconTooltip>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {detectorType !== 'grounding' && (
        <FormField
          control={form.control}
          name="acknowledgeRequired"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-4">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsSaveDetectorAcknowledgeRequiredLabel}
                </FormLabel>
                <InfoIconTooltip>
                  {
                    messages.eventdetectorsSaveDetectorAcknowledgeRequiredTooltip
                  }
                </InfoIconTooltip>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {messages.eventdetectorsSaveDetectorShareWithLabel}
            </FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect
                {...field}
                disabled={mutateType === 'edit' && !isOwner(detector)}
                disabledReason={
                  messages.genericNotOwnerDisabledText ||
                  'You must be the owner to share this object'
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="emails"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between mr-2">
              <FormLabel>
                {messages.eventdetectorsSaveDetectorNotifyEmailLabel}
              </FormLabel>
              <InfoIconTooltip>
                {messages.eventdetectorsSaveDetectorNotifyEmailTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <ListInput
                {...field}
                placeholder={
                  messages.eventdetectorsSaveDetectorNotifyEmailPlaceholder
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="phones"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between mr-2">
              <div className="flex space-x-4 items-center">
                <FormLabel>
                  {messages.eventdetectorsSaveDetectorNotifyPhoneLabel}
                </FormLabel>
              </div>
              <InfoIconTooltip>
                {messages.eventdetectorsSaveDetectorNotifyPhoneTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <ListInput
                {...field}
                placeholder={
                  messages.eventdetectorsSaveDetectorNotifyPhonePlaceholder
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
});

// Add a display name for better debugging
MutateBaseForm.displayName = 'MutateBaseForm';
